"use client";

import {
  getLoadErrorRecoveryKind,
  getLoadErrorSignature,
  type LoadErrorRecoveryKind,
} from "@/lib/load-error-recovery";

const RECOVERY_STORAGE_PREFIX = "hiive:load-error-recovery:";
const attemptedRecoveries = new Set<string>();

async function clearBrowserAppStateForReload() {
  const cleanup: Promise<unknown>[] = [];

  try {
    if ("caches" in window) {
      const cacheNames = await window.caches.keys();
      cleanup.push(...cacheNames.map((name) => window.caches.delete(name)));
    }
  } catch {
    // Cache Storage can be blocked in private browsing. Reload anyway.
  }

  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      cleanup.push(
        ...registrations.map((registration) => registration.unregister()),
      );
    }
  } catch {
    // Service-worker access is best-effort during recovery.
  }

  await Promise.allSettled(cleanup);
}

function markRecoveryAttempted(key: string): boolean {
  if (attemptedRecoveries.has(key)) return false;

  try {
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, "1");
  } catch {
    // Safari privacy modes can throw SecurityError for sessionStorage. The
    // in-memory guard still prevents a reload loop for the current document.
  }

  attemptedRecoveries.add(key);
  return true;
}

/**
 * Recover once per browser session from an incoherent client deployment.
 *
 * This is shared by React error boundaries, global error listeners, failed
 * Next.js assets, and PostHog's recoverable-error hook. Keeping the reload
 * guard in one place prevents those observers from racing into a reload loop.
 */
export function scheduleLoadErrorRecovery(
  error: Error & { digest?: string },
  recoveryKind: LoadErrorRecoveryKind | null = getLoadErrorRecoveryKind(error),
  beforeReload?: () => void,
  {
    /**
     * Whether to drop Cache Storage and the service worker before reloading.
     *
     * On by default, because the errors this was built for ARE stale-asset
     * errors: the cached bundle is the thing that is wrong, so a reload that
     * keeps it just reproduces the failure.
     *
     * Pass `false` when the cache is demonstrably not implicated. It is not a
     * free precaution — `sw.ts` caches only map tiles and `/_next/static/`,
     * and wiping the latter is what turns the next few navigations into
     * uncached chunk fetches on whatever connection just failed. Clearing it
     * to recover from something the cache did not cause makes the page slower
     * for no reason.
     */
    clearAppState = true,
  }: { clearAppState?: boolean } = {},
): boolean {
  if (
    !recoveryKind ||
    typeof window === "undefined" ||
    typeof navigator === "undefined" ||
    !navigator.onLine
  ) {
    return false;
  }

  const signature = getLoadErrorSignature(error) || "unknown";
  const recoveryKey = `${RECOVERY_STORAGE_PREFIX}${signature}`;
  if (!markRecoveryAttempted(recoveryKey)) return false;

  try {
    beforeReload?.();
  } catch {
    // Telemetry flushing must not block recovery.
  }

  if (!clearAppState) {
    window.setTimeout(() => window.location.reload(), 250);
    return true;
  }

  // Cache Storage and service-worker promises can themselves stall in a
  // WebView. Cleanup is best-effort; it must never hold the recovery hostage.
  let reloaded = false;
  const reload = () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };
  const timer = window.setTimeout(reload, 2_000);
  void clearBrowserAppStateForReload().finally(() => {
    window.clearTimeout(timer);
    window.setTimeout(reload, 250);
  });
  return true;
}
